import React, { useEffect, useState } from 'react';
import { collection, query, onSnapshot } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { Building2, ArrowRight, Clock } from 'lucide-react';

interface Facility {
  id: string;
  name: string;
  type: string;
  description: string;
  status: 'available' | 'maintenance' | 'occupied';
  openingHours: any[];
}

interface FacilitiesGridProps {
  onFacilitySelect: (facilityId: string) => void;
}

export const FacilitiesGrid: React.FC<FacilitiesGridProps> = ({ onFacilitySelect }) => {
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const q = query(collection(db, 'facilities'));
    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const facilitiesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Facility[];
        setFacilities(facilitiesData);
      },
      (err) => {
        console.error('Error loading facilities:', err);
        setError('Anlagen konnten nicht geladen werden');
      }
    );

    return () => unsubscribe();
  }, []);

  const getNextOpeningHours = (facility: Facility) => {
    if (!facility.openingHours?.length) {
      return 'Keine Öffnungszeiten verfügbar';
    }

    const now = new Date();
    const currentDay = now.getDay();
    const currentTime = now.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    const adjustedDay = (currentDay + 6) % 7;
    const dayHours = facility.openingHours[adjustedDay];
    
    if (dayHours) {
      if (dayHours.closed) {
        return 'Heute geschlossen';
      }
      if (dayHours.end > currentTime) {
        return `Heute geöffnet bis ${dayHours.end} Uhr`;
      }
    }
    
    for (let i = 1; i <= 7; i++) {
      const nextDay = (adjustedDay + i) % 7;
      const nextDayHours = facility.openingHours.find(h => h.dayOfWeek === nextDay);
      
      if (nextDayHours && !nextDayHours.closed) {
        const days = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
        return `${days[nextDay]}: ${nextDayHours.start} - ${nextDayHours.end} Uhr`;
      }
    }
    
    return 'Keine Öffnungszeiten verfügbar';
  };

  if (error) {
    return (
      <div className="rounded-md bg-red-50 dark:bg-red-900/50 p-4">
        <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
      {facilities.map((facility) => (
        <button
          key={facility.id}
          onClick={() => onFacilitySelect(facility.id)}
          className="w-full text-left bg-white dark:bg-gray-800 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
        >
          <div className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 sm:gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center">
                  <Building2 className="h-5 w-5 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                  <h3 className="ml-2 text-lg font-medium text-gray-900 dark:text-white truncate">
                    {facility.name}
                  </h3>
                </div>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                  {facility.description}
                </p>
              </div>

              <div className="flex-shrink-0">
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
                  ${facility.status === 'available' 
                    ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' 
                    : facility.status === 'maintenance' 
                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' 
                    : 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'}`}
                >
                  {facility.status === 'available' ? 'Verfügbar' :
                   facility.status === 'maintenance' ? 'Wartung' :
                   'Belegt'}
                </span>
              </div>
            </div>

            <div className="mt-4 flex items-center text-sm text-gray-500 dark:text-gray-400">
              <Clock className="h-5 w-5 mr-2 flex-shrink-0" />
              <span className="truncate">{getNextOpeningHours(facility)}</span>
            </div>

            <div className="mt-4 flex items-center text-indigo-600 dark:text-indigo-400 text-sm font-medium">
              Kalender öffnen
              <ArrowRight className="ml-1 h-4 w-4" />
            </div>
          </div>
        </button>
      ))}
    </div>
  );
};